import React, { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";
import { createServerFn } from "@tanstack/react-start";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { getOffersByCoachId } from "~/lib/server/coach";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "~/components/ui/card";
import { getDBWithInit } from "~/lib/db";
import { MembershipPlan, coaches, offers } from "~/lib/schemas";
import { and, eq } from "drizzle-orm";
import { Modal } from "~/components/ui/modal/modal";
import { ProfileEditHeader } from "../../../../../components/profile-edit-header";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { createNoIndexMeta } from "~/lib/shared/meta";

const SetMainOfferSchema = z.object({
  offerId: z.string(),
  coachId: z.string(),
});

const DeleteOfferSchema = z.object({
  offerId: z.string(),
});

export const getCoachOffers = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const offers = await getOffersByCoachId(coach.id);
    return { coach, offers: offers.filter((o) => o.id !== coach.mainOfferId) };
  });

export const setMainOffer = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(SetMainOfferSchema)
  .handler(async ({ data, context: _context }) => {
    const { offerId, coachId } = data;
    await (await getDBWithInit())
      .update(coaches)
      .set({ mainOfferId: offerId })
      .where(eq(coaches.id, coachId));
    return { success: true };
  });

export const createNewOffer = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const offer = await (
      await getDBWithInit()
    )
      .insert(offers)
      .values({
        coachId: coach.id,
        headline: "New Offer",
      })
      .returning();

    return { offerId: offer[0].id };
  });

export const deleteOffer = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(DeleteOfferSchema)
  .handler(async ({ data, context }) => {
    const { offerId } = data;
    const coach = context.coach;
    await (await getDBWithInit())
      .delete(offers)
      .where(and(eq(offers.id, offerId), eq(offers.coachId, coach.id)));
    return { success: true };
  });

export const Route = createFileRoute("/coach/_backend/edit-profile/offers/")({
  head: () =>
    createNoIndexMeta(
      "Manage Offers – Dream Coach Match",
      "Manage and edit your coaching offers and services",
    ),
  component: CoachOffersPage,
});

function CoachOffersPage() {
  const coach = useCoachPrivateProfileRequired();
  const navigate = useNavigate({ from: "/coach/edit-profile/offers" });
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<string | undefined>();

  const { data: offerData } = useQuery({
    queryKey: ["coach-offers"],
    queryFn: () => getCoachOffers(),
  });

  const setMainOfferMutation = useMutation({
    mutationFn: (formData: { offerId: string; coachId: string }) =>
      setMainOffer({ data: formData }),
    onSuccess: () => {
      toast.success("Main offer updated successfully");
      queryClient.invalidateQueries({ queryKey: ["coach-offers"] });
    },
    onError: () => {
      toast.error("Failed to update main offer");
    },
  });

  const createOfferMutation = useMutation({
    mutationFn: () => createNewOffer(),
    onSuccess: (result) => {
      toast.success("New offer created successfully");
      navigate({
        to: "/coach/edit-profile/offers/$offerId",
        params: { offerId: result.offerId },
      });
    },
    onError: () => {
      toast.error("Failed to create new offer");
    },
  });

  const deleteOfferMutation = useMutation({
    mutationFn: (offerId: string) => deleteOffer({ data: { offerId } }),
    onSuccess: () => {
      toast.success("Offer deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["coach-offers"] });
      setShowDeleteModal(false);
      setOfferToDelete(undefined);
    },
    onError: () => {
      toast.error("Failed to delete offer");
    },
  });

  if (!offerData) {
    return <div>Loading...</div>;
  }

  const { offers } = offerData;

  // Only ProPlus members can manage multiple offers
  if (coach.membershipPlan !== MembershipPlan.ProPlus) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <h1 className="font-heading text-2xl">Multiple Offers Management</h1>
        <p className="text-muted-foreground">
          Managing multiple offers is only available for ProPlus members.
        </p>
        <Link to="/coach/membership-plans">
          <Button>Upgrade to ProPlus</Button>
        </Link>
      </div>
    );
  }

  const handleSetMainOffer = (offerId: string) => {
    setMainOfferMutation.mutate({ offerId, coachId: coach.id });
  };

  const handleCreateOffer = () => {
    createOfferMutation.mutate();
  };

  const handleDeleteOffer = () => {
    if (offerToDelete) {
      deleteOfferMutation.mutate(offerToDelete);
    }
  };

  return (
    <>
      <ProfileEditHeader
        title="Coaching Offers"
        description="Create and manage your coaching offers."
        username={coach.username}
      />
      <Button
        type="button"
        onClick={handleCreateOffer}
        disabled={createOfferMutation.isPending}
      >
        Add New Offer
      </Button>
      <div className="mt-4 grid grid-cols-1 gap-4">
        {0 < offers.length ? (
          offers.map((offer) => (
            <Card key={offer.id}>
              <CardContent className="flex items-center justify-between py-6">
                <div className="grow">
                  <div className="flex items-center gap-2">
                    <CardTitle>{offer.headline}</CardTitle>
                    {offer.id === coach.mainOfferId && (
                      <span className="bg-brand-100 text-brand-700 rounded px-2 py-1 text-xs">
                        Main Offer
                      </span>
                    )}
                  </div>
                  <CardDescription>{offer.preHeadline}</CardDescription>
                </div>
                <div className="flex gap-2">
                  {offer.id !== coach.mainOfferId && (
                    <Button
                      variant="outline"
                      onClick={() => handleSetMainOffer(offer.id)}
                      disabled={setMainOfferMutation.isPending}
                    >
                      Set as Main Offer
                    </Button>
                  )}
                  <Link
                    to="/coach/edit-profile/offers/$offerId"
                    params={{ offerId: offer.id }}
                  >
                    <Button variant="outline">Edit</Button>
                  </Link>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setOfferToDelete(offer.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <p className="text-muted-foreground py-8 text-center">
            No offers yet. Click "Add New Offer" to create your first offer.
          </p>
        )}
      </div>
      <Modal.Root open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <Modal.Panel>
          <Modal.Title>Delete Offer</Modal.Title>
          <Modal.Description>
            Are you sure you want to delete this offer? This action cannot be
            undone.
          </Modal.Description>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOfferToDelete(undefined);
                setShowDeleteModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteOfferMutation.isPending}
              onClick={handleDeleteOffer}
            >
              Delete
            </Button>
          </div>
        </Modal.Panel>
      </Modal.Root>
    </>
  );
}
